import React, { useState, useEffect } from 'react';

const STATUS_STYLE = {
    SUCCESS: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    ERROR:   { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
    WARNING: { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
};

const EX_STYLE = {
    NSE: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    BSE: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    MCX: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
};

const TODAY = new Date().toISOString().slice(0, 10);
const SYNC_KEY = 'NAVIABackupDropcopy_BMS_SYNC_2026_SECRET';

export default function SyncMonitor() {
    const [logs,        setLogs]        = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [dateFrom,    setDateFrom]    = useState(TODAY);
    const [dateTo,      setDateTo]      = useState(TODAY);
    const [exchange,    setExchange]    = useState('ALL');
    const [status,      setStatus]      = useState('ALL');

    const loadLogs = async () => {
        try {
            const params = new URLSearchParams({ key: SYNC_KEY });
            if (dateFrom)           params.append('date_from', dateFrom);
            if (dateTo)             params.append('date_to',   dateTo);
            if (exchange !== 'ALL') params.append('exchange',  exchange);
            if (status   !== 'ALL') params.append('status',    status);

            const res  = await fetch('/api/dropcopy/sync-logs?' + params.toString());
            const data = await res.json();
            setLogs(data.logs || []);
            setLastRefresh(new Date());
        } catch (err) {
            console.error('Failed to load sync logs:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLogs();
    }, []);

    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(loadLogs, 30000);
        return () => clearInterval(interval);
    }, [autoRefresh, dateFrom, dateTo, exchange, status]);

    const handleApply = () => { setLoading(true); loadLogs(); };
    const handleReset = () => {
        setDateFrom(TODAY); setDateTo(TODAY);
        setExchange('ALL'); setStatus('ALL');
        setTimeout(loadLogs, 100);
    };

    const fmtTime = (ts) => ts ? new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
    const fmtDur  = (ms) => !ms ? '—' : ms > 1000 ? (ms/1000).toFixed(1)+'s' : ms+'ms';
    const fmtSize = (b)  => !b  ? '—' : b > 1024*1024 ? (b/1024/1024).toFixed(1)+' MB' : Math.round(b/1024)+' KB';

    const todayStr   = TODAY;
    const todayLogs  = logs.filter(l => l.trade_date?.slice(0,10) === todayStr);
    const lastNSECM  = todayLogs.find(l => l.exchange === 'NSE' && l.segment === 'CM' && l.status === 'SUCCESS');
    const lastNSEFO  = todayLogs.find(l => l.exchange === 'NSE' && l.segment === 'FO' && l.status === 'SUCCESS');
    const errorCount = todayLogs.filter(l => l.status === 'ERROR').length;

    const isStale = (log) => {
        if (!log) return true;
        return (Date.now() - new Date(log.log_time)) / 1000 / 60 > 5;
    };

    const SyncCard = ({ label, log }) => {
        const stale = isStale(log);
        return (
            <div style={{ background: '#fff', border: `1px solid ${stale ? '#fecaca' : '#bbf7d0'}`, borderRadius: 10, padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</span>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: stale ? '#fef2f2' : '#f0fdf4', color: stale ? '#dc2626' : '#15803d', border: `1px solid ${stale ? '#fecaca' : '#bbf7d0'}`, fontWeight: 600 }}>
                        {stale ? '⚠ STALE' : '✓ LIVE'}
                    </span>
                </div>
                {log ? (
                    <>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Last sync: <strong style={{ color: '#111' }}>{fmtTime(log.log_time)}</strong></div>
                        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#64748b' }}>
                            <span>Trades: <strong style={{ color: '#1d4ed8' }}>{log.trades_count?.toLocaleString()}</strong></span>
                            <span>Positions: <strong style={{ color: '#15803d' }}>{log.positions_count?.toLocaleString()}</strong></span>
                            <span>Duration: <strong style={{ color: '#374151' }}>{fmtDur(log.sync_duration_ms)}</strong></span>
                        </div>
                    </>
                ) : (
                    <div style={{ fontSize: 12, color: '#dc2626' }}>No sync today</div>
                )}
            </div>
        );
    };

    const inputStyle = { padding: '6px 10px', borderRadius: 6, border: '0.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12, color: '#0f172a', outline: 'none' };

    return (
        <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif" }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>Live Sync Monitor</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        DropCopy sync status from office server → Azure
                        {lastRefresh && <span style={{ marginLeft: 8 }}>· Last refresh: {fmtTime(lastRefresh)}</span>}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
                        Auto-refresh (30s)
                    </label>
                    <button onClick={handleApply} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
                        ↺ Refresh
                    </button>
                </div>
            </div>

            {/* Status Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
                <SyncCard label="NSE CM" log={lastNSECM} />
                <SyncCard label="NSE FO" log={lastNSEFO} />
                <div style={{ background: '#fff', border: `1px solid ${errorCount > 0 ? '#fecaca' : '#e2e8f0'}`, borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Errors Today</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: errorCount > 0 ? '#dc2626' : '#15803d' }}>{errorCount}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>sync failures</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Today Total</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#1d4ed8' }}>{todayLogs.length}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>sync events</div>
                </div>
            </div>

            {/* Filters */}
            <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, padding: '14px 20px', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>From Date</label>
                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>To Date</label>
                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Exchange</label>
                        <select value={exchange} onChange={e => setExchange(e.target.value)} style={inputStyle}>
                            <option value="ALL">All</option>
                            <option value="NSE">NSE</option>
                            <option value="BSE">BSE</option>
                            <option value="MCX">MCX</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Status</label>
                        <select value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
                            <option value="ALL">All</option>
                            <option value="SUCCESS">Success</option>
                            <option value="ERROR">Error</option>
                            <option value="WARNING">Warning</option>
                        </select>
                    </div>
                    <button onClick={handleApply} style={{ ...inputStyle, background: '#1d4ed8', color: '#fff', border: 'none', cursor: 'pointer', alignSelf: 'flex-end' }}>Apply</button>
                    <button onClick={handleReset} style={{ ...inputStyle, cursor: 'pointer', alignSelf: 'flex-end' }}>Reset</button>
                </div>
            </div>

            {/* Log Table */}
            <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Sync Log — Last 100 events</span>
                    <span style={{ fontSize: 11, color: '#64748b' }}>{logs.length} records</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 150px 60px 60px 80px 80px 80px 1fr', padding: '8px 16px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' }}>
                    {['Time','Source','Exch','Seg','Trades','Positions','Duration','Status / Error'].map(h => (
                        <div key={h} style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</div>
                    ))}
                </div>

                {loading ? (
                    <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Loading sync logs...</div>
                ) : logs.length === 0 ? (
                    <div style={{ padding: 48, textAlign: 'center' }}>
                        <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 8 }}>No sync logs found for selected filters.</div>
                        <div style={{ fontSize: 12, color: '#cbd5e1' }}>Logs appear here after each sync from the office server.</div>
                        <div style={{ marginTop: 16, padding: '10px 16px', background: '#fef2f2', border: '0.5px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#dc2626', display: 'inline-block' }}>
                            ⚠ Current status: Office server cannot reach Azure (WinError 10060). IT team needs to fix firewall.
                        </div>
                    </div>
                ) : (
                    logs.map((log, i) => {
                        const st = STATUS_STYLE[log.status] || STATUS_STYLE.SUCCESS;
                        const ex = EX_STYLE[log.exchange] || {};
                        return (
                            <div key={log.id} style={{ display: 'grid', gridTemplateColumns: '160px 150px 60px 60px 80px 80px 80px 1fr', padding: '10px 16px', alignItems: 'center', borderBottom: i < logs.length - 1 ? '0.5px solid #f1f5f9' : 'none', background: log.status === 'ERROR' ? '#fff5f5' : log.status === 'WARNING' ? '#fffbeb' : '#fff' }}>
                                <div style={{ fontSize: 11, color: '#374151' }}>{fmtTime(log.log_time)}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{log.file_source}</div>
                                <div><span style={{ fontSize: 10, fontWeight: 600, padding: '2px 5px', borderRadius: 4, background: ex.bg, color: ex.color, border: `0.5px solid ${ex.border}` }}>{log.exchange}</span></div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{log.segment}</div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8' }}>{log.trades_count?.toLocaleString() || '—'}</div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#15803d' }}>{log.positions_count?.toLocaleString() || '—'}</div>
                                <div style={{ fontSize: 11, color: '#374151' }}>{fmtDur(log.sync_duration_ms)}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: st.bg, color: st.color, border: `0.5px solid ${st.border}` }}>{log.status}</span>
                                    {log.error_message && <span style={{ fontSize: 11, color: '#dc2626' }}>{log.error_message}</span>}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Live URL */}
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', border: '0.5px solid #bbf7d0', borderRadius: 8, fontSize: 11, color: '#166534' }}>
                Direct URL:&nbsp;
                <a href={`/api/dropcopy/sync-logs?key=${SYNC_KEY}`} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', fontWeight: 600 }}>
                    /api/dropcopy/sync-logs?key={SYNC_KEY}
                </a>
                &nbsp;· Auto-refreshes every 30 seconds
            </div>
        </div>
    );
}